import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import React, { ReactNode, useEffect, useState } from 'react';
import { useRef } from 'react';

import { useSettings } from './context/SettingsContext';
import { useSharedHistoryContext } from './context/SharedHistoryContext';
import ActionsPlugin from './plugins/ActionsPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import CharacterStylesPopupPlugin from './plugins/CharacterStylesPopupPlugin';
import ClickableLinkPlugin from './plugins/ClickableLinkPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import EmojisPlugin from './plugins/EmojisPlugin';
import KeywordsPlugin from './plugins/KeywordsPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import MarkdownShortcutPlugin from './plugins/MarkdownShortcutPlugin';
import SpeechToTextPlugin from './plugins/SpeechToTextPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin';
import ContentEditable from './ui/ContentEditable';
import Placeholder from './ui/Placeholder';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import EditorContext from './context/EditorContext';
import { LexicalEditor } from 'lexical';
import { useTranslation } from 'react-i18next';
import DragDropPaste from './plugins/DragDropPastePlugin';
import EmojiPickerPlugin from './plugins/EmojiPickerPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';

import EquationsPlugin from './plugins/EquationsPlugin';
import { LayoutPlugin } from './plugins/LayoutPlugin';
import PageBreakPlugin from './plugins/PageBreakPlugin';

import CollapsiblePlugin from './plugins/CollapsiblePlugin';

import BoxPlugin from './plugins/BoxPlugin';
import TableWidthPlugin from './plugins/TableWidthPlugin';

interface IEditorProps {
  children?: ReactNode;
  hashtagsEnabled?: boolean;
  autoLinkEnabled?: boolean;
  emojisEnabled?: boolean;
  emojiPickerEnabled?: boolean;
  actionsEnabled?: boolean;
  placeholder?: string;
  listMaxIndent?: number;
  isEditable?: boolean;
  /**
   * 마운트 직후 본문에 커서를 넣을지. 기본은 넣는다 — 편집기 전용 모달(문제·풀이 등록/수정)은
   * 열자마자 쓰는 것이 목적이라 그게 맞다.
   * 끄는 쪽은 '편집기가 화면의 일부'인 자리다. OCR 검수 모달은 문제를 열 때마다 번호칸에
   * 포커스를 주어(OcrReviewModal의 numberInputRef) ←→·Enter 로 훑게 돼 있는데, 이 자동
   * 포커스가 그 커서를 매번 빼앗아 두 설계가 충돌했다.
   */
  autoFocusEnabled?: boolean;
  locale?: 'en' | 'fr' | 'ptBr' | 'ru' | 'tr' | 'de' | null;
  onChange?: (editorState: string, editorInstance?: LexicalEditor) => void;
}

const Editor = ({
  children,
  hashtagsEnabled = false,
  autoLinkEnabled = false,
  emojisEnabled = false,
  emojiPickerEnabled = false,
  actionsEnabled = false,
  listMaxIndent = 7,
  placeholder = '',
  isEditable = true,
  autoFocusEnabled = true,
  locale = null,
  onChange,
}: IEditorProps) => {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);

  const editorStateRef = useRef(null);
  const { historyState } = useSharedHistoryContext();
  const {
    settings: { isRichText },
  } = useSettings();
  const placeholderComponent = <Placeholder>{placeholder}</Placeholder>;

  const { i18n } = useTranslation();

  useEffect(() => {
    editor.setEditable(isEditable);

    if (locale) i18n.changeLanguage(locale);
  }, []);

  return (
    <EditorContext.Provider
      value={{ initialEditor: editor, activeEditor, setActiveEditor }}
    >
      {children}
      <div className={`editor-container`}>
        {autoFocusEnabled && <AutoFocusPlugin />}
        <ClearEditorPlugin />
        {hashtagsEnabled && <HashtagPlugin />}
        {emojisEnabled && <EmojisPlugin />}
        {emojiPickerEnabled && <EmojiPickerPlugin />}
        <KeywordsPlugin />
        <SpeechToTextPlugin />
        <DragDropPaste />
        {autoLinkEnabled && <AutoLinkPlugin />}

        <>
          <RichTextPlugin
            contentEditable={<ContentEditable />}
            placeholder={placeholderComponent}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ImagesPlugin />
          <OnChangePlugin
            onChange={(editorState) => {
              onChange?.(JSON.stringify(editorState), activeEditor);
              return (editorStateRef.current = editorState);
            }}
          />
          <MarkdownShortcutPlugin />
          <CodeHighlightPlugin />
          <ListPlugin />
          <CheckListPlugin />
          <ListMaxIndentLevelPlugin maxDepth={listMaxIndent} />
          <LinkPlugin />
          <ClickableLinkPlugin />
          <CharacterStylesPopupPlugin />
          <TabFocusPlugin />
          <EquationsPlugin />
          <LayoutPlugin />
          <PageBreakPlugin />
          <CollapsiblePlugin />
          <BoxPlugin />
          {/* 크기가 정해진 표를 비율로 — [보기](읽기 전용)에도 있어야 해서 [Insert] 메뉴가 아니라 여기 둔다 */}
          <TableWidthPlugin />
        </>

        <HistoryPlugin externalHistoryState={historyState} />
        {actionsEnabled && <ActionsPlugin isRichText={isRichText} />}
      </div>
    </EditorContext.Provider>
  );
};

export default Editor;
